import { FC, MouseEvent, TouchEvent, useRef, useState } from 'react'
import clsx from 'clsx'

interface ICardPhotoCarousel {
  images: string[]
  alt: string
  className?: string
}

const MAX_PHOTOS = 5
const SWIPE_THRESHOLD = 30

// Listing-card photo strip: on desktop moving the mouse across the image
// flips through the photos (each horizontal segment maps to one frame, the
// way rieltor/OLX cards behave); on touch a horizontal swipe steps through
// them. Dots show the position.
const CardPhotoCarousel: FC<ICardPhotoCarousel> = ({ images, alt, className }) => {
  const [index, setIndex] = useState(0)
  const touchStart = useRef<{ x: number; y: number } | null>(null)
  const shown = images.slice(0, MAX_PHOTOS)

  if (shown.length === 0) {
    return <div className={clsx('bg-brand-soft', className)} />
  }

  const handleMouseMove = (event: MouseEvent<HTMLDivElement>) => {
    if (shown.length < 2) return
    const rect = event.currentTarget.getBoundingClientRect()
    const ratio = (event.clientX - rect.left) / rect.width
    const next = Math.min(shown.length - 1, Math.max(0, Math.floor(ratio * shown.length)))
    setIndex(next)
  }

  const handleTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    const touch = event.touches[0]
    touchStart.current = { x: touch.clientX, y: touch.clientY }
  }

  const handleTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
    if (!touchStart.current || shown.length < 2) return
    const touch = event.changedTouches[0]
    const dx = touch.clientX - touchStart.current.x
    const dy = touch.clientY - touchStart.current.y
    touchStart.current = null
    // Vertical intent keeps scrolling the page; only a clear horizontal
    // swipe flips the photo.
    if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) < Math.abs(dy)) return
    event.preventDefault()
    setIndex((current) =>
      dx < 0 ? Math.min(shown.length - 1, current + 1) : Math.max(0, current - 1),
    )
  }

  return (
    <div
      // Owns its horizontal swipes — page-level swipe navigation skips it.
      data-noswipe
      className={clsx('relative overflow-hidden', className)}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setIndex(0)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {shown.map((src, i) => (
        <img
          key={`${src}-${i}`}
          src={src}
          alt={alt}
          loading="lazy"
          className={clsx(
            'absolute inset-0 w-full h-full object-cover transition-opacity duration-150 motion-reduce:transition-none',
            i === index ? 'opacity-100' : 'opacity-0',
          )}
        />
      ))}
      {shown.length > 1 && (
        <div className="absolute bottom-2 inset-x-0 flex justify-center gap-1">
          {shown.map((_, i) => (
            <span
              key={i}
              className={clsx(
                'w-1.5 h-1.5 rounded-full transition-colors duration-150',
                i === index ? 'bg-surface' : 'bg-surface bg-opacity-50',
              )}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default CardPhotoCarousel
